import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, NavLink, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutGrid, Settings, Users, LogOut, ExternalLink, Menu as MenuIcon, X, CreditCard, KeyRound, Star, BarChart3, CalendarCheck } from "lucide-react";
import api from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";
import { SHOW_SUBSCRIPTION_SECTION } from "@/config";

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hasSite, setHasSite] = useState(false);

  useEffect(() => {
    api.get("/restaurant").then((r) => setRestaurant(r.data)).catch(() => {});
    api.get("/bookings/access").then((r) => setHasSite(!!r.data.has_site)).catch(() => {});
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const navItems = [
    { to: "/admin", label: "Menù", icon: LayoutGrid, end: true },
    { to: "/admin/statistiche", label: "Statistiche", icon: BarChart3 },
    ...(SHOW_SUBSCRIPTION_SECTION ? [{ to: "/admin/abbonamento", label: "Abbonamento", icon: CreditCard }] : []),
    { to: "/admin/recensioni", label: "Recensioni", icon: Star },
    ...(hasSite ? [{ to: "/admin/prenotazioni", label: "Prenotazioni", icon: CalendarCheck }] : []),
    { to: "/admin/impostazioni", label: "Impostazioni", icon: Settings },
    ...(user?.role === "owner" ? [{ to: "/admin/staff", label: "Staff", icon: Users }] : []),
  ];

  const SidebarContent = () => (
    <>
      <div className="flex items-center gap-2 font-cabinet font-bold text-xl px-2 mb-8">
        <BrandLogo className="w-8 h-8" /> Cube
      </div>
      <nav className="space-y-1 flex-1">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={() => setMobileOpen(false)}
            data-testid={`nav-${item.label.toLowerCase()}`}
            className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-[#002FA7] text-white" : "text-muted-foreground hover:bg-muted"}`}
          >
            <item.icon className="w-4.5 h-4.5" /> {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="space-y-2 pt-4 border-t border-border">
        {restaurant && (
          <a href={`/m/${restaurant.slug}`} target="_blank" rel="noopener noreferrer" data-testid="view-public-menu" className="flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm text-[#002FA7] font-medium hover:bg-muted transition-colors">
            <ExternalLink className="w-4 h-4" /> Vedi menù pubblico
          </a>
        )}
        <div className="px-3 py-2 text-xs text-muted-foreground">
          <p className="font-semibold text-foreground truncate">{user?.name}</p>
          <p className="truncate">{user?.email}</p>
        </div>
        <button onClick={handleLogout} data-testid="logout-btn" className="w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors">
          <LogOut className="w-4 h-4" /> Esci
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA] font-plex flex">
      <aside className="hidden md:flex w-64 bg-white border-r border-border p-4 flex-col fixed inset-y-0">
        <SidebarContent />
      </aside>

      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-white border-b border-border h-14 flex items-center justify-between px-4">
        <div className="flex items-center gap-2 font-cabinet font-bold text-lg"><BrandLogo className="w-6 h-6" /> Cube</div>
        <button onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn"><MenuIcon className="w-6 h-6" /></button>
      </div>

      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-64 bg-white p-4 flex flex-col">
            <button onClick={() => setMobileOpen(false)} className="absolute top-4 right-4" data-testid="mobile-close"><X className="w-5 h-5" /></button>
            <SidebarContent />
          </aside>
        </div>
      )}

      <main className="flex-1 md:ml-64 pt-14 md:pt-0 min-w-0">
        {user?.must_change_password && (
          <div className="bg-amber-50 border-b border-amber-200 px-5 py-3 text-sm text-amber-900 flex items-center gap-2 flex-wrap" data-testid="pwd-reminder-banner">
            <KeyRound className="w-4 h-4 flex-shrink-0" />
            <span>Stai usando una password temporanea. Per sicurezza,</span>
            <Link to="/admin/impostazioni" onClick={() => setMobileOpen(false)} className="font-semibold underline hover:text-amber-950">impostane una nuova ora</Link>
          </div>
        )}
        <div className="max-w-5xl mx-auto p-5 md:p-8">
          <Outlet context={{ restaurant, setRestaurant }} />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
